import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Download, Home, Mail, XCircle } from 'lucide-react';
import { CONTACT_EMAIL, PaymentStatus } from 'types';
import { getPaymentStatus } from '../lib/api';
import { useBooking } from '../state/BookingContext';

const POLL_MS = 3000;
const MAX_POLLS = 20;

const Contact = () => (
  <p className="text-sm text-gray-600 mt-6">
    Questions? Get in touch with the Sanskriti team at <a className="underline text-orange-700" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
  </p>
);

const Welcome = () => (
  <p className="text-gray-700 mt-4">
    Thank you from all of us at Sanskriti e.V.! Your seat at the table is reserved, and we are so happy you will celebrate Durga Puja 2026 with us. Come with an open heart and an empty stomach, and let us make these five days
    in Hamburg feel like home. Shubho Pujo!
  </p>
);

/** Where Stripe sends the user back to. The outcome always comes from Stripe (via payment-status), never from the URL. */
export default function ResultPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { draft, reset } = useBooking();
  const paymentIntentId = params.get('payment_intent');
  const free = params.get('free');
  const [result, setResult] = useState<PaymentStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (free) return reset();
    if (!paymentIntentId) return navigate('/', { replace: true });
    let cancelled = false;
    (async () => {
      for (let i = 0; i < MAX_POLLS && !cancelled; i++) {
        try {
          const s = await getPaymentStatus(paymentIntentId);
          if (cancelled) return;
          if (s.status !== 'pending') {
            setResult(s);
            if (s.status === 'paid') reset(); // booking is complete: forget the draft
            return;
          }
        } catch (e) {
          if (!cancelled) setError((e as Error).message);
          return;
        }
        await new Promise((r) => setTimeout(r, POLL_MS));
      }
      if (!cancelled) setTimedOut(true);
    })();
    return () => { cancelled = true; };
  }, [paymentIntentId, free]);

  const box = 'max-w-2xl mx-auto bg-white rounded-xl shadow-sm border border-gray-100 p-6';

  if (free) {
    return (
      <div className={box}>
        <CheckCircle className="w-14 h-14 text-green-500" />
        <h2 className="text-2xl font-bold text-green-700 mt-2">Your reservation is confirmed</h2>
        <p className="text-gray-700 mt-3">There was nothing to pay, so no invoice was created. Your booking is saved with us.</p>
        <Welcome />
        <Contact />
      </div>
    );
  }

  if (error || timedOut) {
    return (
      <div className={box}>
        <h2 className="text-xl font-bold text-gray-800">We could not confirm your payment yet</h2>
        <p className="text-gray-700 mt-3">
          {error ?? 'Your payment is still being processed by your bank.'} Please do not pay again. If it went through, you will receive your invoice by email.
        </p>
        <Contact />
      </div>
    );
  }

  if (!result) return <div className={box}><p className="text-gray-700">Confirming your payment with Stripe…</p></div>;

  if (result.status === 'paid') {
    return (
      <div className={box}>
        <CheckCircle className="w-14 h-14 text-green-500" />
        <h2 className="text-2xl font-bold text-green-700 mt-2">Payment successful</h2>
        <Welcome />
        <div className="mt-5 flex flex-wrap gap-3">
          {result.invoicePdf && (
            <a href={result.invoicePdf} target="_blank" rel="noopener noreferrer" download
              className="inline-flex items-center bg-green-600 text-white px-5 py-3 rounded-lg font-semibold hover:bg-green-700">
              <Download className="w-5 h-5 mr-2" />Download invoice{result.invoiceNumber ? ` ${result.invoiceNumber}` : ''}
            </a>
          )}
          <a href="https://sanskriti-hamburg.de" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center border border-gray-300 text-gray-700 px-5 py-3 rounded-lg font-semibold hover:bg-gray-50">
            <Home className="w-5 h-5 mr-2" />Visit Sanskriti e.V.
          </a>
        </div>
        <p className="text-sm text-gray-700 mt-4 flex items-start gap-2">
          <Mail className="w-4 h-4 mt-0.5 shrink-0" />A copy of your invoice has been sent to {result.email ?? 'your email address'}. Please keep a copy, printed or digital, with you at the event.
        </p>
        <Contact />
      </div>
    );
  }

  const f = result.failure!;
  const canRetry = draft.invoice?.paymentIntentId === paymentIntentId;
  return (
    <div className={box}>
      <XCircle className="w-14 h-14 text-red-500" />
      <h2 className="text-2xl font-bold text-red-700 mt-2">Payment failed</h2>
      <p className="text-gray-700 mt-3">Your booking has not been made and you have not been charged.</p>
      <div role="alert" className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
        <p className="font-semibold">Why: {f.message}</p>
        {f.bankMessage && <p className="mt-1">Details from the bank: {f.bankMessage}</p>}
        {(f.code || f.declineCode) && <p className="mt-1 text-xs text-red-700">Reference: {[f.code, f.declineCode].filter(Boolean).join(' / ')}</p>}
      </div>
      <div className="mt-5 flex flex-wrap gap-3">
        {canRetry && <button onClick={() => navigate('/checkout')} className="bg-green-600 text-white px-5 py-3 rounded-lg font-semibold hover:bg-green-700">Try payment again</button>}
        <Link to="/" onClick={() => !canRetry && reset()} className="px-5 py-3 rounded-lg font-semibold border border-gray-300 text-gray-700">{canRetry ? 'Change my booking' : 'Start a new booking'}</Link>
      </div>
      <Contact />
    </div>
  );
}
